/**
 * Desktop top bar (INVENTORY-DESKTOP §2.2; `design/Games Tonight.dc.html`, the
 * `hdrDesktop` block — re-cut on 2026-09-03, DESIGN-CHANGELOG "MY GAMES: the
 * page about you", the *Account chip* and *Nav label* revisions).
 *
 * 58px tall, `padding:0 26px`, `gap:28px`, sticky at `z-index:40` — the ONLY
 * sticky element on the whole desktop page (§1.5).
 *
 * Layout, left to right:
 *   coin          the home mark. It goes to the board; it wears no underline.
 *   party lockup  `CrewSwitcher` — the party's name and its menu.
 *   divider
 *   nav items     ME · TONIGHT? · DISCOVER · PLAYTESTS · PARTY
 *   spacer
 *   round chip    wordless, only while a round exists
 *   What's New    `◆ WHAT'S NEW` + an optional count badge
 *   account chip  26px avatar + your name + `▾` — A MENU, not a link
 *
 * WHAT LEFT. The pixel-font `GAMES TONIGHT?` wordmark, which was the Tonight
 * tab: the board is an ordinary mono nav item now, second after ME, and the
 * coin is the home mark. The lockup's `homeBd` underline went with it.
 *
 * THE ACCOUNT CHIP IS A MENU. Role · party and the STEAM line above EDIT
 * PROFILE and SIGN OUT — the two actions that used to sit in MY GAMES' rail
 * and, before that, on the profile page. Same dismissal contract as the party
 * switcher beside it: a document-level CAPTURE click that spares `[data-me]`,
 * Escape, and every navigation closing it on the way out (`nav()` clears
 * `meOpen`).
 *
 * QUARTER ICON: the call site passes NEITHER `u` NOR `hover`, so per
 * DC-RUNTIME §1 the renderVals fallbacks apply — `u=1.5`, `hover=true`. The
 * coin therefore SPINS ON HOVER here, unlike both mobile call sites and the
 * desktop login (`u=2 hover=false`). Do not "fix" this by passing `hover`.
 *
 * `profile` and `detail` are absent from the nav on purpose (profile via the
 * menu, detail via any tile), and the per-item count badge the template sketches
 * is a dead branch (§9.4) — no item can ever have a count — so it is not built.
 */

import { useEffect } from 'react';

import { Avatar } from '../components/Avatar.tsx';
import CrewSwitcher from './components/CrewSwitcher.tsx';
import { useEscape } from './components/Modal.tsx';
import { QuarterIcon } from '../components/QuarterIcon.tsx';
import type { Screen } from '../lib/types.ts';
import { useQd } from '../store/index.ts';
import { desktopNavVM } from '../store/selectors.desktop.ts';
import { roundChipVM } from '../store/selectors.round.ts';
import { accountMenuVM, headerVM } from '../store/selectors.ts';

const MONO = 'var(--gt-font-mono)';

/** The account chip and its dropdown (`design/Games Tonight.dc.html`, `data-me`). */
function AccountMenu(): JSX.Element {
  const s = useQd();
  const h = headerVM(s);
  const v = desktopNavVM(s);
  const m = accountMenuVM(s);

  useEscape(() => s.closeMe(), m.open);

  useEffect(() => {
    if (!m.open) return;
    // CAPTURE phase, matching the switcher: a click that opens something else
    // must still close this, and capture runs before that handler can stop
    // propagation. `[data-me]` is the whitelist.
    const onDoc = (e: MouseEvent): void => {
      const el = e.target as Element | null;
      if (el?.closest?.('[data-me]')) return;
      s.closeMe();
    };
    document.addEventListener('click', onDoc, true);
    return () => document.removeEventListener('click', onDoc, true);
  }, [m.open, s]);

  return (
    <div data-me="1" style={{ position: 'relative', flex: 'none' }}>
      <div
        onClick={() => s.toggleMe()}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          whiteSpace: 'nowrap',
          border: '1px solid ' + m.meBd,
          padding: '5px 10px 5px 6px',
          cursor: 'pointer',
        }}
      >
        <Avatar
          style={{ flex: 'none' }}
          size={26}
          fs={8}
          ini={h.meIni}
          bg={h.meHueBg}
          hasAv={h.meHasAv}
          avBg={h.meAvBg}
        />
        <div style={{ fontFamily: MONO, fontSize: 11, color: 'var(--gt-text-2)' }}>{v.meName}</div>
        <span style={{ fontFamily: MONO, fontSize: 9, color: 'var(--gt-muted)' }}>▾</span>
      </div>
      {m.open ? (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            minWidth: 190,
            background: 'var(--gt-card)',
            border: '1px solid var(--gt-border)',
            boxShadow: 'var(--gt-shadow-toast)',
            zIndex: 60,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              padding: '9px 12px',
              borderBottom: '1px solid var(--gt-line)',
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <span style={{ fontFamily: MONO, fontSize: '8.5px', letterSpacing: '.07em', color: 'var(--gt-faint)' }}>
              {m.roleLine}
            </span>
            <span
              style={{
                fontFamily: MONO,
                fontSize: '8.5px',
                letterSpacing: '.06em',
                color: 'var(--gt-muted)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {m.steamLine}
            </span>
          </div>
          <div
            onClick={() => s.nav('profile')}
            className="d-hov-menu-acc"
            style={{
              padding: '11px 12px',
              fontFamily: MONO,
              fontSize: '10.5px',
              letterSpacing: '.04em',
              color: 'var(--gt-text-3)',
              cursor: 'pointer',
            }}
          >
            EDIT PROFILE ▸
          </div>
          <div
            onClick={() => void s.signOut()}
            className="d-hov-menu-acc2"
            style={{
              padding: '11px 12px',
              borderTop: '1px solid var(--gt-line-0)',
              fontFamily: MONO,
              fontSize: '10.5px',
              letterSpacing: '.04em',
              color: 'var(--gt-text-5)',
              cursor: 'pointer',
            }}
          >
            SIGN OUT
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function TopNav(): JSX.Element {
  const s = useQd();
  const h = headerVM(s);
  const v = desktopNavVM(s);
  const rc = roundChipVM(s);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 28,
        padding: '0 26px',
        height: 58,
        borderBottom: '1px solid var(--gt-line)',
        background: 'var(--gt-bg-hd)',
        position: 'sticky',
        top: 0,
        zIndex: 40,
        boxSizing: 'border-box',
      }}
    >
      {/* coin · party lockup · divider --------------------------------------- */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, height: 58, flex: 'none' }}>
        <div
          onClick={() => s.nav('tonight')}
          data-tip="GAMES TONIGHT?"
          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          <QuarterIcon />
        </div>
        <CrewSwitcher />
        <div style={{ width: 1, height: 22, background: 'var(--gt-line)' }} />
      </div>

      {/* nav ------------------------------------------------------------- */}
      <div
        style={{
          display: 'flex',
          gap: 24,
          alignItems: 'center',
          fontFamily: MONO,
          fontSize: 12,
          letterSpacing: '.06em',
        }}
      >
        {v.navItems.map((n) => (
          <div
            key={n.key}
            onClick={() => s.nav(n.key as Screen)}
            style={{
              color: n.clr,
              borderBottom: '2px solid ' + n.bd,
              padding: '19px 0 17px',
              cursor: 'pointer',
              display: 'flex',
              gap: 7,
              alignItems: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            {n.l}
          </div>
        ))}
      </div>

      <div style={{ flex: 1 }} />

      {/* round chip (3039, changelog 2026-08-26 "Desktop round chip matches
          the phone"): wordless, 34px, the coin says it by being there. */}
      {rc.rChipOn ? (
        <div
          onClick={() => (rc.rChipAct === 'open' ? s.rOpen() : void s.rJoin())}
          data-tip={rc.rChipTip}
          data-tip-pos="r"
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 34,
            height: 34,
            flex: 'none',
            border: '1px solid var(--acc)',
            background: rc.rChipBg,
            cursor: 'pointer',
            boxSizing: 'border-box',
          }}
        >
          <QuarterIcon u={1.6} hover={false} />
          {rc.rChipPulse ? (
            <span
              style={{
                position: 'absolute',
                top: -5,
                right: -5,
                width: 10,
                height: 10,
                background: 'var(--acc)',
                border: '1px solid var(--gt-bg-hd)',
                animation: 'gt-rpulse 1.1s steps(1) infinite',
              }}
            />
          ) : null}
        </div>
      ) : null}

      {/* what's new ------------------------------------------------------- */}
      <div
        onClick={() => s.openWhatsNew()}
        data-tip="GAMES ADDED SINCE YOU LAST REVIEWED"
        data-tip-pos="r"
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          border: '1px solid ' + h.wnBd,
          color: h.wnClr,
          padding: '7px 11px',
          cursor: 'pointer',
          fontFamily: MONO,
          fontSize: 11,
          letterSpacing: '.04em',
          whiteSpace: 'nowrap',
          flex: 'none',
        }}
      >
        ◆ WHAT&apos;S NEW
        {h.hasNew ? (
          <span
            style={{
              background: 'var(--gt-info)',
              color: 'var(--gt-ink)',
              fontFamily: 'var(--gt-font-pixel)',
              fontSize: 8,
              padding: '3px 5px',
            }}
          >
            {h.newCount}
          </span>
        ) : null}
      </div>

      {/* account chip — a menu ------------------------------------------- */}
      <AccountMenu />
    </div>
  );
}

export default TopNav;
